import { LicenseAnalyticsFact } from '../../services/dashboardService';

/**
 * Cross-filtering for the Analytics → Licences tab (User Story 107),
 * mirroring `cloudAnalyticsModel.ts` (User Story 100).
 *
 * The server ships one fact row per license (`license-facts`, gated on
 * `analytics.licenses.view`, no cost fields); this module re-derives the
 * cross-filterable charts — by software, vendor, type, status — from those
 * rows under a selection, so clicking a slice costs nothing but a re-render.
 *
 * Unlike Cloud, there is no separate client-side date range here — the
 * Licences tab's own `AnalyticsAdvancedFilters` drawer already narrows the
 * fact fetch server-side, so a fact row is already inside whatever the page
 * filter selected.
 */

export type LicenseDimension = 'software' | 'vendor' | 'type' | 'status';

/** Selected keys per dimension. Within a dimension OR, across dimensions AND. */
export type LicenseSelection = Partial<Record<LicenseDimension, string[]>>;

export const LICENSE_DIMENSION_LABELS: Record<LicenseDimension, string> = {
    software: 'Software / Product',
    vendor: 'Vendor',
    type: 'Type',
    status: 'Status',
};

// Dimension name and fact field name only coincide for 'vendor'/'type'; the
// 'software' dimension maps to `softwareName` on the fact, not `software`
// (there is no such field). Indexing `fact[dimension]` directly for
// 'software' silently read `undefined`, which `String()` turned into the
// literal string "undefined" — stored as the selection key and shown in the
// chip label. This map is the single place that mapping lives now.
const DIMENSION_FIELD: Record<LicenseDimension, keyof LicenseAnalyticsFact> = {
    software: 'softwareName',
    vendor: 'vendor',
    type: 'type',
    status: 'status',
};

const keyOf = (fact: LicenseAnalyticsFact, dimension: LicenseDimension): string => (
    String(fact[DIMENSION_FIELD[dimension]])
);

const labelOf = (fact: LicenseAnalyticsFact, dimension: LicenseDimension): string => (
    dimension === 'status' ? fact.statusLabel : String(fact[DIMENSION_FIELD[dimension]])
);

/** Looks up the display label for a selected key — used to render filter chips. */
export function labelForKey(
    facts: LicenseAnalyticsFact[],
    dimension: LicenseDimension,
    key: string,
): string {
    const fact = facts.find((f) => keyOf(f, dimension) === key);
    return fact ? labelOf(fact, dimension) : key;
}

export const isSelected = (
    selection: LicenseSelection,
    dimension: LicenseDimension,
    key: string,
): boolean => (selection[dimension] ?? []).includes(key);

export const selectionCount = (selection: LicenseSelection): number =>
    Object.values(selection).reduce((total, keys) => total + (keys?.length ?? 0), 0);

/**
 * Adds or removes one key. `additive` (ctrl/cmd/shift-click) keeps the rest
 * of the dimension's keys; a plain click replaces them, which is what
 * "select this one" means to everyone who has not read a manual.
 */
export function toggleSelection(
    selection: LicenseSelection,
    dimension: LicenseDimension,
    key: string,
    additive: boolean,
): LicenseSelection {
    const current = selection[dimension] ?? [];
    const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : additive ? [...current, key] : [key];

    const updated: LicenseSelection = { ...selection };
    if (next.length === 0) delete updated[dimension];
    else updated[dimension] = next;
    return updated;
}

/**
 * Rows matching every selected dimension except `exclude`.
 *
 * Excluding the visual's own dimension is what keeps a chart usable after you
 * click it: filter the software bar by its own selection and it collapses to
 * a single slice with no way back to the others.
 */
export function applySelection(
    facts: LicenseAnalyticsFact[],
    selection: LicenseSelection,
    exclude?: LicenseDimension,
): LicenseAnalyticsFact[] {
    const dimensions = (Object.keys(selection) as LicenseDimension[])
        .filter((d) => d !== exclude && (selection[d]?.length ?? 0) > 0);
    if (dimensions.length === 0) return facts;
    return facts.filter((fact) =>
        dimensions.every((d) => selection[d]!.includes(keyOf(fact, d))),
    );
}

export interface LicenseAnalyticsBreakdownRow {
    key: string;
    label: string;
    count: number;
    totalSeats: number;
}

function breakdown(
    facts: LicenseAnalyticsFact[],
    dimension: LicenseDimension,
): LicenseAnalyticsBreakdownRow[] {
    const rows = new Map<string, LicenseAnalyticsBreakdownRow>();
    facts.forEach((fact) => {
        const key = keyOf(fact, dimension);
        const existing = rows.get(key);
        if (existing) {
            existing.count += 1;
            existing.totalSeats += fact.totalSeats;
        } else {
            rows.set(key, { key, label: labelOf(fact, dimension), count: 1, totalSeats: fact.totalSeats });
        }
    });
    return [...rows.values()].sort((a, b) => b.totalSeats - a.totalSeats);
}

export interface LicenseAnalyticsMonthPoint { month: string; count: number }

/** YYYY-MM the date falls in, or null if outside [start, start+11months). */
function monthOf(date: string | null, monthKeys: string[]): string | null {
    if (!date) return null;
    const key = date.slice(0, 7);
    return monthKeys.includes(key) ? key : null;
}

/** 12 trailing YYYY-MM keys ending this month, oldest first. */
export function trailingMonths(): string[] {
    const now = new Date();
    const months: string[] = [];
    for (let back = 11; back >= 0; back -= 1) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 1));
        months.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
    }
    return months;
}

/** 12 forward YYYY-MM keys starting this month, earliest first. */
export function forwardMonths(): string[] {
    const now = new Date();
    const months: string[] = [];
    for (let fwd = 0; fwd <= 11; fwd += 1) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + fwd, 1));
        months.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
    }
    return months;
}

export interface LicenseAnalyticsView {
    bySoftware: LicenseAnalyticsBreakdownRow[];
    byVendor: LicenseAnalyticsBreakdownRow[];
    byType: LicenseAnalyticsBreakdownRow[];
    byStatus: LicenseAnalyticsBreakdownRow[];
    purchasedVsAssigned: { label: string; count: number }[];
    overVsUnderLicensed: { label: string; count: number }[];
    purchaseTrend: LicenseAnalyticsMonthPoint[];
    expiryTimeline: LicenseAnalyticsMonthPoint[];
    scopedCount: number;
}

export function aggregate(
    facts: LicenseAnalyticsFact[],
    selection: LicenseSelection,
): LicenseAnalyticsView {
    const scoped = applySelection(facts, selection);

    const purchaseMonths = trailingMonths();
    const expiryMonths = forwardMonths();

    const purchaseTrend: LicenseAnalyticsMonthPoint[] = purchaseMonths.map((month) => ({
        month,
        count: scoped.filter((f) => monthOf(f.purchaseDate, purchaseMonths) === month).length,
    }));
    const expiryTimeline: LicenseAnalyticsMonthPoint[] = expiryMonths.map((month) => ({
        month,
        count: scoped.filter((f) => f.status !== 'expired' && monthOf(f.expiryDate, expiryMonths) === month).length,
    }));

    const purchased = scoped.reduce((sum, f) => sum + f.totalSeats, 0);
    const assigned = scoped.reduce((sum, f) => sum + f.usedSeats, 0);

    let over = 0, under = 0, fully = 0;
    scoped.forEach((f) => {
        if (f.usedSeats > f.totalSeats) over += 1;
        else if (f.usedSeats < f.totalSeats) under += 1;
        else fully += 1;
    });

    return {
        // Each breakdown ignores its own dimension's selection — see applySelection.
        bySoftware: breakdown(applySelection(facts, selection, 'software'), 'software'),
        byVendor: breakdown(applySelection(facts, selection, 'vendor'), 'vendor'),
        byType: breakdown(applySelection(facts, selection, 'type'), 'type'),
        byStatus: breakdown(applySelection(facts, selection, 'status'), 'status'),
        purchasedVsAssigned: [
            { label: 'Purchased', count: purchased },
            { label: 'Assigned', count: assigned },
        ],
        overVsUnderLicensed: [
            { label: 'Over-licensed', count: over },
            { label: 'Under-licensed', count: under },
            { label: 'Fully licensed', count: fully },
        ],
        purchaseTrend,
        expiryTimeline,
        scopedCount: scoped.length,
    };
}
